import api from './api';

export type ContentType =
  | 'news_article'
  | 'tts_job'
  | 'stt_job'
  | 'audio_translation_job'
  | 'text_translation_job'
  | 'video_translation_job'
  | 'audio_to_video_job'
  | 'clip_generation_job'
  | 'youtube_publish_job'
  | 'social_publish_job'
  | 'thumbnail_generation_job';

export type AssigneeType = 'user' | 'team';

export interface Assignee {
  id: string;
  name: string;
  email?: string;
}

export interface Assignment {
  assigneeType: AssigneeType;
  assignee: Assignee | null;
  assignedAt: string;
  assignedBy: string;
}

export interface Viewer {
  userId: string;
  name?: string;
  email?: string;
  lastViewedAt: string;
  viewCount: number;
}

export interface ActivityEntry {
  _id: string;
  actorId: { _id: string; name: string; email: string } | string;
  action: 'viewed' | 'updated' | 'assigned' | 'reassigned' | 'commented' | 'created' | 'linked'
    | 'review_requested' | 'review_approved' | 'review_rejected';
  metadata?: Record<string, unknown>;
  createdAt: string;
}

export interface ActivityData {
  viewers: Viewer[];
  timeline: ActivityEntry[];
}

export type ReviewDecision = 'pending' | 'approved' | 'rejected';

export interface ReviewRound {
  _id: string;
  reviewerType: AssigneeType;
  reviewer: Assignee | null;
  status: ReviewDecision;
  comment?: string;
  requestedBy: string;
  decidedBy?: string;
  decidedByUser?: Assignee;
  decidedAt?: string;
  createdAt: string;
}

export interface ReviewStatus {
  current: ReviewRound | null;
  history: ReviewRound[];
}

export interface Comment {
  _id: string;
  authorId: { _id: string; name: string; email: string; avatar?: string } | string;
  body: string;
  parentCommentId?: string;
  createdAt: string;
  editedAt?: string;
}

export interface LineageNode {
  contentType: ContentType;
  contentId: string;
  title: string;
  status?: string;
  key: string;
}

export interface LineageEdge {
  from: string;
  to: string;
  relation: 'translated_to' | 'rendered_to' | 'clipped_to' | 'published_to';
}

const base = (contentType: ContentType, contentId: string) => `/content/${contentType}/${contentId}`;

export interface MyAssignment {
  kind: 'assignment' | 'review';
  reviewId?: string;
  contentType: ContentType;
  contentId: string;
  title: string;
  status?: string;
  assigneeType: AssigneeType;
  assigneeName: string;
  assignedAt: string;
  assignedBy: { id: string; name?: string; email?: string } | null;
}

export const CONTENT_TYPE_LABEL: Record<ContentType, string> = {
  news_article: 'News Article',
  tts_job: 'Text to Speech',
  stt_job: 'Speech to Text',
  audio_translation_job: 'Audio Translation',
  text_translation_job: 'Text Translation',
  video_translation_job: 'Video Translation',
  audio_to_video_job: 'Create Video',
  clip_generation_job: 'Clip Generation',
  youtube_publish_job: 'YouTube Publish',
  social_publish_job: 'Social Publish',
  thumbnail_generation_job: 'Thumbnail Generation',
};

// Exact per-item detail route, where one exists.
export const CONTENT_TYPE_DETAIL_ROUTE: Partial<Record<ContentType, (id: string) => string>> = {
  news_article: (id) => `/app/newsroom/articles/${id}`,
  audio_to_video_job: (id) => `/app/create-video?jobId=${id}`,
};

// Fallback list page for content types with no standalone detail route —
// still lands the user somewhere useful rather than a dead link.
export const CONTENT_TYPE_LIST_ROUTE: Record<ContentType, string> = {
  news_article: '/app/newsroom',
  tts_job: '/app/audio/text-to-speech',
  stt_job: '/app/audio/speech-to-text',
  audio_translation_job: '/app/audio/translation',
  text_translation_job: '/app/audio/text-translation',
  video_translation_job: '/app/audio/video-translation',
  audio_to_video_job: '/app/create-video',
  clip_generation_job: '/app/clips',
  youtube_publish_job: '/app/videos',
  social_publish_job: '/app/scheduled-posts',
  thumbnail_generation_job: '/app/thumbnails',
};

export const getContentRoute = (contentType: ContentType, contentId: string): string =>
  CONTENT_TYPE_DETAIL_ROUTE[contentType]?.(contentId) || CONTENT_TYPE_LIST_ROUTE[contentType];

export interface NextAction {
  label: string;
  targetContentType: ContentType;
  route: string;
  eligible: boolean;
  reason?: string;
}

export const contentApi = {
  recordView: async (contentType: ContentType, contentId: string): Promise<void> => {
    await api.post(`${base(contentType, contentId)}/view`);
  },

  getActivity: async (contentType: ContentType, contentId: string): Promise<ActivityData> => {
    const res = await api.get<{ success: boolean; data: ActivityData }>(`${base(contentType, contentId)}/activity`);
    return res.data.data;
  },

  getAssignment: async (contentType: ContentType, contentId: string): Promise<Assignment | null> => {
    const res = await api.get<{ success: boolean; data: Assignment | null }>(`${base(contentType, contentId)}/assignment`);
    return res.data.data;
  },

  assign: async (contentType: ContentType, contentId: string, assigneeType: AssigneeType, assigneeId: string): Promise<void> => {
    await api.post(`${base(contentType, contentId)}/assign`, { assigneeType, assigneeId });
  },

  getComments: async (contentType: ContentType, contentId: string): Promise<Comment[]> => {
    const res = await api.get<{ success: boolean; data: Comment[] }>(`${base(contentType, contentId)}/comments`);
    return res.data.data;
  },

  addComment: async (contentType: ContentType, contentId: string, body: string, parentCommentId?: string): Promise<Comment> => {
    const res = await api.post<{ success: boolean; data: Comment }>(`${base(contentType, contentId)}/comments`, { body, parentCommentId });
    return res.data.data;
  },

  deleteComment: async (contentType: ContentType, contentId: string, commentId: string): Promise<void> => {
    await api.delete(`${base(contentType, contentId)}/comments/${commentId}`);
  },

  getLineage: async (contentType: ContentType, contentId: string): Promise<{ nodes: LineageNode[]; edges: LineageEdge[] }> => {
    const res = await api.get<{ success: boolean; data: { nodes: LineageNode[]; edges: LineageEdge[] } }>(`${base(contentType, contentId)}/lineage`);
    return res.data.data;
  },

  getMyAssignments: async (): Promise<MyAssignment[]> => {
    const res = await api.get<{ success: boolean; data: MyAssignment[] }>('/content/assignments/mine');
    return res.data.data;
  },

  getReviewStatus: async (contentType: ContentType, contentId: string): Promise<ReviewStatus> => {
    const res = await api.get<{ success: boolean; data: ReviewStatus }>(`${base(contentType, contentId)}/review`);
    return res.data.data;
  },

  requestReview: async (contentType: ContentType, contentId: string, reviewerType: AssigneeType, reviewerId: string): Promise<void> => {
    await api.post(`${base(contentType, contentId)}/review/request`, { reviewerType, reviewerId });
  },

  decideReview: async (contentType: ContentType, contentId: string, reviewId: string, decision: 'approved' | 'rejected', comment?: string): Promise<void> => {
    await api.post(`${base(contentType, contentId)}/review/${reviewId}/decide`, { decision, comment });
  },

  getNextActions: async (contentType: ContentType, contentId: string): Promise<NextAction[]> => {
    const res = await api.get<{ success: boolean; data: NextAction[] }>(`${base(contentType, contentId)}/next-actions`);
    return res.data.data;
  },
};
